/**
 * @file OrderItemRelinkFlow.tsx
 * @description Привязка позиции заказа к товару каталога: выбор SKU →
 * подтверждение → запрос (phase19.24 шаг 5, OQ-54).
 *
 * Два шага, а не один: привязка на стадии «На сборке» пересобирает задание и
 * сбрасывает отметки кладовщика — такое нельзя делать по случайному тапу в
 * списке товаров. Текст обещания собирает реестр `lib/order-item-link.ts`,
 * чтобы подтверждение и тост говорили об одном и том же.
 *
 * Пикер переиспользован (`SkuPickerDialog`): своего списка товаров у вкладки
 * состава быть не должно — разошлись бы фасовка, цена и признак «нет в
 * наличии».
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { SkuPickerDialog } from "./SkuPickerDialog";
import { orderItemName, type OrderItemRawDto } from "../../lib/api-order-items";
import {
  formatMoney,
  relinkOrderItem,
  type OrderSkuDto,
} from "../../lib/api-orders";
import {
  ORDER_ITEM_UNLINKED_TEXT,
  relinkConfirmText,
  relinkErrorText,
} from "../../lib/order-item-link";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";

interface Props {
  orderId: string;
  /** Позиция без товара каталога, которую привязываем. */
  item: OrderItemRawDto;
  onClose: () => void;
  /** Привязка прошла: перечитать состав, заказ и путь. */
  onDone: () => void;
}

export function OrderItemRelinkFlow({ orderId, item, onClose, onDone }: Props) {
  const notify = useNotify();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [sku, setSku] = useState<OrderSkuDto | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirm(): Promise<void> {
    if (!sku) return;
    setBusy(true);
    try {
      const res = await relinkOrderItem(orderId, item.id, sku.id);
      // Номер задания сервер отдаёт не всегда — тогда говорим общими словами,
      // но про пересборку сообщаем обязательно: отметки кладовщика сброшены.
      const taskLabel = res.pickingTaskNumber
        ? `задание ${res.pickingTaskNumber}`
        : "задание сборки";
      notify.success(
        res.pickingTaskId
          ? `Позиция привязана, ${taskLabel} пересобрано`
          : "Позиция привязана",
        res.pickingTaskId && slug
          ? {
              action: {
                label: "Открыть задание",
                onClick: () =>
                  void navigate(`/shop/${slug}/wms/picking/${res.pickingTaskId}`),
              },
            }
          : undefined,
      );
      onDone();
      onClose();
    } catch (err) {
      notify.error(relinkErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!sku) {
    return (
      <SkuPickerDialog
        single
        onClose={onClose}
        onSelectMany={(list) => setSku(list[0] ?? null)}
      />
    );
  }

  const skuName = sku.displayName || sku.productName;

  return (
    <AlertDialog open onOpenChange={(v) => (!v && !busy ? onClose() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>{ORDER_ITEM_UNLINKED_TEXT.confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {relinkConfirmText({
              itemName: orderItemName(item),
              skuName,
              priceGross: formatMoney(item.priceGross),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* «Выбрать другой» возвращает в пикер: ошибиться товаром легко. */}
          <Button
            variant="ghost"
            className="min-h-12"
            disabled={busy}
            onClick={() => setSku(null)}
          >
            Выбрать другой
          </Button>
          <Button className="min-h-12" disabled={busy} onClick={() => void confirm()}>
            {ORDER_ITEM_UNLINKED_TEXT.action}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
